import React, { useEffect, useMemo, useState } from 'react';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { customerApi } from '../Onboarding/CustomerOnboarding/customerApi';
import { getTenantInfo } from '../OrgManagement/employeeApiService';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';
import Input from '../ui/input';
import { billApi } from './billApi';

const createEmptyItemRow = () => ({
  item: '',
  quantity: '',
  pricePerUnit: '',
});

const DISCOUNT_RULES = {
  SAVE10: { type: 'percentage', value: 10 },
  SAVE20: { type: 'percentage', value: 20 },
  FLAT100: { type: 'flat', value: 100 },
};

const resolveSiteValue = (site) => {
  return site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? '';
};

const resolveSiteLabel = (site) => {
  return site?.site_name || site?.name || site?.siteCode || site?.site_code || `Site ${site?.site_id ?? site?.id ?? ''}`;
};

const BillCustomer = () => {
  // Log screen view
  useScreenLogging('BillCustomer', {
    component: 'BillCustomer',
  });

  const [tableNumber, setTableNumber] = useState('');
  const [items, setItems] = useState([createEmptyItemRow()]);
  const [discountCode, setDiscountCode] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [customerSelection, setCustomerSelection] = useState(''); // 'new' or customer_id
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    mobile: '',
    email: '',
  });
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [taxRates, setTaxRates] = useState({
    cgst: '',
    sgst: '',
  });
  const [isMobileView, setIsMobileView] = useState(false);
  
  // API integration state
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState({ success: '', error: '' });
  const [discountValidation, setDiscountValidation] = useState({ isValid: null, message: '' });
  const [tenantInfo, setTenantInfo] = useState(null);
  const [isLoadingTenant, setIsLoadingTenant] = useState(false);
  
  // Get overlay functions from context
  const { openOverlay } = useOverlay();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (event) => setIsMobileView(event.matches);
    handleChange(mediaQuery);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Fetch sites
  useEffect(() => {
    let isMounted = true;
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        if (isMounted) {
          setSites(sorted);
        }
      } catch (error) {
        if (isMounted) {
          setSitesError(error?.message || 'Unable to load sites. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };
    fetchSites();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch customers
  useEffect(() => {
    let isMounted = true;
    const fetchCustomers = async () => {
      setIsCustomersLoading(true);
      setCustomersError('');
      try {
        const result = await customerApi.listCustomers({ limit: 100, status: 'active' });
        if (result.success) {
          const sorted = [...(result.customers || [])].sort((a, b) =>
            (a?.customer_name || a?.name || '').localeCompare(b?.customer_name || b?.name || '')
          );
          if (isMounted) {
            setCustomers(sorted);
          }
        } else {
          if (isMounted) {
            setCustomersError(result.error || 'Failed to load customers');
          }
        }
      } catch (error) {
        if (isMounted) {
          setCustomersError(error?.message || 'Unable to load customers. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsCustomersLoading(false);
        }
      }
    };
    fetchCustomers();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch tenant info
  useEffect(() => {
    let isMounted = true;
    const fetchTenantInfo = async () => {
      setIsLoadingTenant(true);
      try {
        const tenantData = await getTenantInfo();
        if (isMounted) {
          setTenantInfo(tenantData);
        }
      } catch (error) {
        console.error('Error fetching tenant info:', error);
        if (isMounted) {
          // Set default values if tenant fetch fails
          setTenantInfo(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingTenant(false);
        }
      }
    };
    fetchTenantInfo();
    return () => {
      isMounted = false;
    };
  }, []);

  const computeRowSubtotal = (row) => {
    const qty = parseFloat(row.quantity) || 0;
    const price = parseFloat(row.pricePerUnit) || 0;
    return qty * price;
  };

  const subtotal = useMemo(
    () => items.reduce((sum, row) => sum + computeRowSubtotal(row), 0),
    [items]
  );

  const discountValue = useMemo(() => {
    if (!discountCode.trim() || subtotal === 0) {
      return 0;
    }
    const rule = DISCOUNT_RULES[discountCode.trim().toUpperCase()];
    if (!rule) {
      return 0;
    }
    if (rule.type === 'percentage') {
      return (subtotal * rule.value) / 100;
    }
    return Math.min(rule.value, subtotal);
  }, [discountCode, subtotal]);

  const discountedSubtotal = Math.max(subtotal - discountValue, 0);
  const cgstAmount = useMemo(() => {
    const cgstRate = parseFloat(taxRates.cgst) || 0;
    return (discountedSubtotal * cgstRate) / 100;
  }, [discountedSubtotal, taxRates.cgst]);

  const sgstAmount = useMemo(() => {
    const sgstRate = parseFloat(taxRates.sgst) || 0;
    return (discountedSubtotal * sgstRate) / 100;
  }, [discountedSubtotal, taxRates.sgst]);

  const grandTotal = discountedSubtotal + cgstAmount + sgstAmount;

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddItem = () => {
    setItems((prev) => [...prev, createEmptyItemRow()]);
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      setItems([createEmptyItemRow()]);
      return;
    }
    setItems((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const handleCustomerChange = (field, value) => {
    setCustomerDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleTaxChange = (field, value) => {
    if (Number(value) < 0) {
      return;
    }
    setTaxRates((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyDiscount = () => {
    const code = discountCode.trim().toUpperCase();
    
    if (!code) {
      setDiscountValidation({
        isValid: false,
        message: 'Please enter a discount code'
      });
      return;
    }

    const rule = DISCOUNT_RULES[code];
    
    if (!rule) {
      setDiscountValidation({
        isValid: false,
        message: 'Invalid discount code. Please try again.'
      });
      return;
    }

    // Valid code found
    const discountAmount = rule.type === 'percentage' 
      ? (subtotal * rule.value) / 100 
      : Math.min(rule.value, subtotal);
    
    setDiscountValidation({
      isValid: true,
      message: `Discount code "${code}" applied successfully! ${rule.type === 'percentage' ? `${rule.value}%` : `₹${rule.value}`} discount applied.`
    });
  };

  const formatCurrency = (amount) =>
    Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!selectedSite) {
      setSubmitFeedback({ success: '', error: 'Please select a site' });
      return;
    }

    if (!tableNumber.trim()) {
      setSubmitFeedback({ success: '', error: 'Please enter a table number' });
      return;
    }

    if (items.length === 0 || items.some(item => !item.item || !item.quantity || !item.pricePerUnit)) {
      setSubmitFeedback({ success: '', error: 'Please add at least one valid item with name, quantity, and price' });
      return;
    }

    if (customerSelection === 'existing' && !selectedCustomerId) {
      setSubmitFeedback({ success: '', error: 'Please select a customer' });
      return;
    }

    if (customerSelection === 'new' && !customerDetails.name.trim()) {
      setSubmitFeedback({ success: '', error: 'Please enter customer name' });
      return;
    }

    setIsSubmitting(true);
    setSubmitFeedback({ success: '', error: '' });

    try {
      // Generate order ID
      const orderId = `ORD-${Date.now().toString().slice(-6)}`;
      
      // Get site details
      const site = sites.find(s => resolveSiteValue(s) === selectedSite);
      const siteName = resolveSiteLabel(site);
      const siteId = selectedSite;
      const siteAddress = site?.address || site?.site_address || '';
      
      // Get organization name from tenant info
      const organizationName = tenantInfo?.data?.organization_name || tenantInfo?.name || tenantInfo?.organization_name || 'Innoworx Dining Hub';

      // Map items to API format
      const mappedItems = items
        .filter(item => item.item && item.quantity && item.pricePerUnit)
        .map((row) => {
          const quantity = parseFloat(row.quantity) || 0;
          const pricePerUnit = parseFloat(row.pricePerUnit) || 0;
          const subtotal = quantity * pricePerUnit;
          
          return {
            name: row.item.trim(),
            quantity: quantity,
            price_per_unit: pricePerUnit,
            subtotal: subtotal,
            unit: 'pcs', // Default unit, can be extended if needed
          };
        });

      // Build API payload
      // Ensure all numeric values are proper numbers, not strings
      const apiPayload = {
        order_id: orderId,
        site_id: siteId,
        items: mappedItems,
        subtotal: parseFloat(subtotal.toFixed(2)),
        total: parseFloat(grandTotal.toFixed(2)),
      };

      // Add optional fields only if they have values
      if (tableNumber.trim()) {
        apiPayload.table_number = tableNumber.trim();
      }
      if (siteName) {
        apiPayload.site_name = siteName;
      }
      if (cgstAmount > 0) {
        apiPayload.cgst = parseFloat(cgstAmount.toFixed(2));
      }
      if (sgstAmount > 0) {
        apiPayload.sgst = parseFloat(sgstAmount.toFixed(2));
      }
      // Include discount_code and discount_amount only if discount code is provided and has value
      if (discountCode.trim()) {
        apiPayload.discount_code = discountCode.trim();
        // Only include discount_amount if it's greater than 0
        // Backend might not accept discount_amount: 0
        if (discountValue > 0) {
          apiPayload.discount_amount = parseFloat(discountValue.toFixed(2));
        }
      }

      // Add customer - prefer customer_id if selected, otherwise use customer object
      // Note: If customer_id is provided but doesn't exist, API will return 400, not 500
      if (customerSelection === 'existing' && selectedCustomerId) {
        apiPayload.customer_id = selectedCustomerId;
        console.log('Using existing customer ID:', selectedCustomerId);
      } else if (customerSelection === 'new' || (!customerSelection && customerDetails.name.trim())) {
        // Use customer object for new customers
        apiPayload.customer = {
          name: customerDetails.name.trim(),
          mobile: customerDetails.mobile.trim() || undefined,
          email: customerDetails.email.trim() || undefined,
        };
        console.log('Using new customer object:', apiPayload.customer);
      } else {
        // No customer provided - API might require customer, but let's try without it
        console.warn('No customer provided - API might require customer');
      }

      console.log('Creating bill with payload:', JSON.stringify(apiPayload, null, 2));
      console.log('Bill API payload details:', {
        order_id: apiPayload.order_id,
        site_id: apiPayload.site_id,
        items_count: apiPayload.items?.length,
        items: apiPayload.items,
        customer_id: apiPayload.customer_id,
        customer: apiPayload.customer,
        subtotal: apiPayload.subtotal,
        total: apiPayload.total,
      });

      // Create bill via API
      const result = await billApi.createBill(apiPayload);

      if (result.success) {
        setSubmitFeedback({ 
          success: `Bill created successfully! Bill Number: ${result.bill?.bill_number || 'N/A'}`,
          error: '' 
        });
        
        // Also open the print overlay with the created bill data
        const billPayload = {
          orderNumber: orderId,
          billNumber: result.bill?.bill_number || `BILL-${Math.floor(100000 + Math.random() * 900000)}`,
          tableNumber,
          items: items.map((row) => ({
            ...row,
            subtotal: computeRowSubtotal(row),
          })),
          subtotal,
          discountCode: discountCode.trim(),
          discountValue,
          discountedSubtotal,
          cgstRate: taxRates.cgst,
          sgstRate: taxRates.sgst,
          cgstAmount,
          sgstAmount,
          grandTotal,
          customerDetails: customerSelection === 'existing' && selectedCustomerId
            ? (result.bill?.customer || customerDetails)
            : customerDetails,
          generatedAt: new Date().toISOString(),
          apiBill: result.bill,
          // Add site details for print
          siteDetails: {
            organizationName: organizationName,
            siteName: siteName,
            address: siteAddress,
          },
        };
        
        console.log('Opening billing print overlay with billPayload:', billPayload);
        console.log('Bill payload keys:', Object.keys(billPayload));
        console.log('Bill payload items:', billPayload.items);
        console.log('Site details:', billPayload.siteDetails);
        
        openOverlay('billingPrint', {
          bill: billPayload,
          formatCurrency: formatCurrency
        });
      } else {
        throw new Error(result.error || 'Failed to create bill');
      }
    } catch (error) {
      console.error('Error creating bill:', error);
      setSubmitFeedback({ 
        success: '', 
        error: error.message || 'Failed to create bill. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBillReady =
    selectedSite.trim().length > 0 &&
    tableNumber.trim().length > 0 &&
    (customerSelection === 'existing' ? selectedCustomerId.trim().length > 0 : customerDetails.name.trim().length > 0) &&
    items.some((row) => computeRowSubtotal(row) > 0);

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="mx-auto flex w-full max-w-7xl flex-col gap-6"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card.Glassmorphism className="p-4">
            <label className="mb-1 block text-sm font-semibold text-gray-600">
              Site <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
              disabled={isSitesLoading}
              required
            >
              <option value="" disabled hidden>Select a site</option>
              {sites.map((site) => (
                <option key={resolveSiteValue(site)} value={resolveSiteValue(site)}>
                  {resolveSiteLabel(site)}
                </option>
              ))}
            </select>
            {sitesError && (
              <p className="mt-1 text-xs text-red-600">{sitesError}</p>
            )}
            {!isSitesLoading && !sites.length && !sitesError && (
              <p className="mt-1 text-xs text-gray-500">
                No sites available. Please create a site first.
              </p>
            )}
          </Card.Glassmorphism>

          <Card.Glassmorphism className="p-4">
            <label className="mb-1 block text-sm font-semibold text-gray-600">
              Table Number <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="Enter table number"
              value={tableNumber}
              onChange={(event) => setTableNumber(event.target.value)}
              required
            />
          </Card.Glassmorphism>

          <Card.Glassmorphism className="p-4">
            <p className="text-sm text-gray-500">Order Number</p>
            <h4 className="text-base font-semibold text-gray-600">
              Will be generated automatically
            </h4>
          </Card.Glassmorphism>
          <Card.Glassmorphism className="p-4">
            <p className="text-sm text-gray-500">Bill Number</p>
            <h4 className="text-base font-semibold text-gray-600">
              Will be generated automatically
            </h4>
          </Card.Glassmorphism>
        </div>

        <Card.Light className="p-3">
          <div className="mb-4 flex flex-col gap-2">
            <h2 className="text-2xl font-semibold text-gray-900">
              Order Items
            </h2>
            <p className="text-sm text-gray-500">
              Add every ordered item with quantity and unit price.
            </p>
          </div>

          <div className="relative">
            {!isMobileView && (
              <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-semibold">Item</th>
                      <th className="px-4 py-3 font-semibold">Quantity</th>
                      <th className="px-4 py-3 font-semibold">Price per Unit</th>
                      <th className="px-4 py-3 font-semibold text-right">
                        Subtotal
                      </th>
                      <th className="px-4 py-3 text-right font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, index) => {
                      const rowSubtotal = computeRowSubtotal(row);
                      return (
                        <tr
                          key={`item-row-${index}`}
                          className="border-t border-gray-100"
                        >
                          <td className="px-4 py-3 align-top">
                            <Input
                              placeholder="Enter item"
                              value={row.item}
                              onChange={(event) =>
                                handleItemChange(index, 'item', event.target.value)
                              }
                              className="rounded-full border border-green-200 bg-white px-4 py-2 text-gray-800 placeholder:text-gray-400"
                            />
                          </td>
                          <td className="px-4 py-3 align-top">
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              placeholder="0"
                              value={row.quantity}
                              onChange={(event) =>
                                handleItemChange(index, 'quantity', event.target.value)
                              }
                              className="rounded-full border border-green-200 bg-white px-4 py-2 text-gray-800 placeholder:text-gray-400"
                            />
                          </td>
                          <td className="px-4 py-3 align-top">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={row.pricePerUnit}
                              onChange={(event) =>
                                handleItemChange(
                                  index,
                                  'pricePerUnit',
                                  event.target.value
                                )
                              }
                              className="rounded-full border border-green-200 bg-white px-4 py-2 text-gray-800 placeholder:text-gray-400"
                            />
                          </td>
                          <td className="px-4 py-3 text-right align-top text-base font-semibold text-gray-800">
                            {formatCurrency(rowSubtotal)}
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="rounded-full border border-red-100 px-3 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-gray-200 bg-gray-50 text-gray-700">
                      <td colSpan={5} className="px-4 py-3">
                        <button
                          type="button"
                          onClick={handleAddItem}
                          className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                        >
                          Add Item
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {isMobileView && (
              <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-2 shadow-sm sm:p-1">
                {items.map((row, index) => {
                  const rowSubtotal = computeRowSubtotal(row);
                  return (
                    <div
                      key={`item-card-${index}`}
                      className="rounded-2xl border border-gray-100 p-3 sm:p-4"
                    >
                      <div className="mb-2 flex items-center justify-between sm:mb-3">
                        <p className="text-sm font-semibold text-gray-700">
                          Item #{index + 1}
                        </p>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="space-y-3 sm:space-y-4">
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Item
                          </label>
                          <Input
                            placeholder="Enter item"
                            value={row.item}
                            onChange={(event) =>
                              handleItemChange(index, 'item', event.target.value)
                            }
                            className="rounded-2xl border border-green-200 bg-gray-50 px-4 py-2 text-gray-800 placeholder:text-gray-400"
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Quantity
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              placeholder="0"
                              value={row.quantity}
                              onChange={(event) =>
                                handleItemChange(index, 'quantity', event.target.value)
                              }
                              className="rounded-2xl border border-green-200 bg-gray-50 px-4 py-2 text-gray-800 placeholder:text-gray-400"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Price per Unit
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={row.pricePerUnit}
                              onChange={(event) =>
                                handleItemChange(
                                  index,
                                  'pricePerUnit',
                                  event.target.value
                                )
                              }
                              className="rounded-2xl border border-green-200 bg-gray-50 px-4 py-2 text-gray-800 placeholder:text-gray-400"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm font-semibold text-gray-700">
                          <span>Subtotal</span>
                          <span>{formatCurrency(rowSubtotal)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                  >
                    Add Item
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card.Light>

        <Card.Light className="p-6">
          <div className="mb-4 flex flex-col gap-2">
            <h3 className="text-xl font-semibold text-gray-900">Discount</h3>
            <p className="text-sm text-gray-500">
              Apply a discount code to reduce the subtotal before taxes.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-500">
                Discount Code
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter discount code"
                  value={discountCode}
                  onChange={(event) => {
                    setDiscountCode(event.target.value);
                    // Clear validation when user types
                    if (discountValidation.isValid !== null) {
                      setDiscountValidation({ isValid: null, message: '' });
                    }
                  }}
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={handleApplyDiscount}
                  className="rounded-2xl border border-green-500 bg-green-600 px-6 py-2 text-sm font-semibold text-white transition hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500/40 whitespace-nowrap"
                >
                  Apply
                </button>
              </div>
              {discountValidation.isValid === true && (
                <p className="mt-2 text-xs text-green-600 font-medium">
                  {discountValidation.message}
                </p>
              )}
              {discountValidation.isValid === false && (
                <p className="mt-2 text-xs text-red-600 font-medium">
                  {discountValidation.message}
                </p>
              )}
              {discountValidation.isValid === null && (
                <p className="mt-2 text-xs text-gray-500">
                  Try SAVE10, SAVE20 or FLAT100 (demo codes)
                </p>
              )}
            </div>
            <div className="flex flex-col justify-end">
              <div className="flex items-center justify-between rounded-2xl border border-green-100 bg-green-50 px-4 py-3 text-sm">
                <span className="font-semibold text-gray-600">
                  Discount Value
                </span>
                <span className="text-lg font-semibold text-green-700">
                  {formatCurrency(discountValue)}
                </span>
              </div>
            </div>
          </div>
        </Card.Light>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card.Light className="p-6">
            <h3 className="text-xl font-semibold text-gray-900">Customer</h3>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-500">
                  Customer Type
                </label>
                <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerSelection('existing');
                      setSelectedCustomerId('');
                      setCustomerDetails({ name: '', mobile: '', email: '' });
                    }}
                    className={`rounded-full px-4 py-1 text-xs font-semibold transition ${
                      customerSelection === 'existing'
                        ? 'bg-green-600 text-white'
                        : 'text-gray-600'
                    }`}
                  >
                    Existing Customer
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerSelection('new');
                      setSelectedCustomerId('');
                    }}
                    className={`rounded-full px-4 py-1 text-xs font-semibold transition ${
                      customerSelection === 'new'
                        ? 'bg-green-600 text-white'
                        : 'text-gray-600'
                    }`}
                  >
                    New Customer
                  </button>
                </div>
              </div>

              {customerSelection === 'existing' ? (
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-500">
                    Select Customer
                  </label>
                  <select
                    value={selectedCustomerId}
                    onChange={(e) => {
                      setSelectedCustomerId(e.target.value);
                      const customer = customers.find(c => (c.customer_id || c.id) === e.target.value);
                      if (customer) {
                        setCustomerDetails({
                          name: customer.customer_name || customer.name || '',
                          mobile: customer.contact_mobile || customer.contact_phone || customer.mobile || '',
                          email: customer.contact_email || customer.email || '',
                        });
                      }
                    }}
                    className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    disabled={isCustomersLoading}
                  >
                    <option value="" disabled hidden>Select a customer</option>
                    {customers.map((customer) => (
                      <option key={customer.customer_id || customer.id} value={customer.customer_id || customer.id}>
                        {customer.customer_name || customer.name || 'Unknown Customer'}
                      </option>
                    ))}
                  </select>
                  {customersError && (
                    <p className="mt-1 text-xs text-red-600">{customersError}</p>
                  )}
                  {!isCustomersLoading && !customers.length && !customersError && (
                    <p className="mt-1 text-xs text-gray-500">
                      No customers available. Please create a customer first.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      placeholder="Enter customer name"
                      value={customerDetails.name}
                      onChange={(event) =>
                        handleCustomerChange('name', event.target.value)
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Mobile Number
                    </label>
                    <Input
                      type="tel"
                      placeholder="Enter mobile number"
                      value={customerDetails.mobile}
                      onChange={(event) =>
                        handleCustomerChange('mobile', event.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      Email
                    </label>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={customerDetails.email}
                      onChange={(event) =>
                        handleCustomerChange('email', event.target.value)
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </Card.Light>

          <Card.Light className="p-6">
            <h3 className="text-xl font-semibold text-gray-900">
              Bill Summary
            </h3>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between text-gray-600">
                <span>Subtotal</span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatCurrency(subtotal)}
                </span>
              </div>
              <div className="flex items-center justify-between text-gray-600">
                <span>Discount</span>
                <span className="text-lg font-semibold text-emerald-600">
                  - {formatCurrency(discountValue)}
                </span>
              </div>
              <div className="flex items-center justify-between text-gray-600">
                <span>Subtotal after discount</span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatCurrency(discountedSubtotal)}
                </span>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="mb-3 text-sm font-semibold text-gray-700">
                  Taxes
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      CGST (%)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="0"
                      value={taxRates.cgst}
                      onChange={(event) =>
                        handleTaxChange('cgst', event.target.value)
                      }
                    />
                  </div>
                  <div className="text-right text-lg font-semibold text-gray-900">
                    {formatCurrency(cgstAmount)}
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <label className="mb-1 block text-xs font-semibold text-gray-500">
                      SGST (%)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="0"
                      value={taxRates.sgst}
                      onChange={(event) =>
                        handleTaxChange('sgst', event.target.value)
                      }
                    />
                  </div>
                  <div className="text-right text-lg font-semibold text-gray-900">
                    {formatCurrency(sgstAmount)}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-gray-100 pt-4 text-gray-900">
                <span className="text-xl font-semibold">Total</span>
                <span className="text-2xl font-bold text-emerald-700">
                  {formatCurrency(grandTotal)}
                </span>
              </div>
            </div>
          </Card.Light>
        </div>

        <div className="flex flex-col gap-3">
          {submitFeedback.success && (
            <div className="rounded-2xl bg-green-50 border border-green-200 p-4">
              <p className="text-sm text-green-700">{submitFeedback.success}</p>
            </div>
          )}
          {submitFeedback.error && (
            <div className="rounded-2xl bg-red-50 border border-red-200 p-4">
              <p className="text-sm text-red-700">{submitFeedback.error}</p>
            </div>
          )}
          <div className="flex justify-end">
            <PrimaryButton
              type="submit"
              disabled={!isBillReady || isSubmitting}
              className="w-full max-w-xs"
            >
              {isSubmitting ? 'Creating Bill...' : 'Generate Bill'}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </>
  );
};

export default BillCustomer;

